import React, { useState, useEffect } from 'react';
import { Box, Button, Menu, MenuItem, ListItemIcon, ListItemText, Divider, TextField, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
} from '@mui/icons-material';

/**
 * Saved filter-view dropdown for list pages. Persists view state to
 * `localStorage` under `savedViews:<key>` so it survives reloads and is
 * per-user (storage is browser-local).
 *
 * Props:
 *   storageKey  — unique per list page (e.g. 'po-list', 'gr-list')
 *   currentState — the filter/search/sort object to snapshot
 *   onApply     — (state) => void; called when a saved view is selected
 *
 * View shape: { id, name, state, createdAt }
 *
 * Active view is detected by deep-equal match against currentState.
 */
function loadAll(key) {
  try { return JSON.parse(localStorage.getItem(`savedViews:${key}`) || '[]'); }
  catch { return []; }
}
function saveAll(key, views) {
  localStorage.setItem(`savedViews:${key}`, JSON.stringify(views));
}
function deepEqual(a, b) { try { return JSON.stringify(a) === JSON.stringify(b); } catch { return false; } }

export function SavedViews({ storageKey, currentState, onApply, size = 'small' }) {
  const [views, setViews] = useState(() => loadAll(storageKey));
  const [anchor, setAnchor] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  // Reload from storage if storageKey changes (rare but cheap).
  useEffect(() => { setViews(loadAll(storageKey)); }, [storageKey]);

  const activeView = views.find(v => deepEqual(v.state, currentState));

  const saveCurrent = () => {
    const name = newName.trim();
    if (!name) return;
    const newView = { id: `${Date.now()}`, name, state: currentState, createdAt: new Date().toISOString() };
    const next = [...views, newView];
    setViews(next);
    saveAll(storageKey, next);
    setCreating(false);
    setNewName('');
    setAnchor(null);
  };
  const deleteView = (id) => {
    const next = views.filter(v => v.id !== id);
    setViews(next);
    saveAll(storageKey, next);
  };
  const applyView = (view) => {
    onApply?.(view.state);
    setAnchor(null);
  };

  const label = activeView ? activeView.name : (views.length === 0 ? 'Save view' : 'Views');

  return (
    <>
      <Button
        size={size}
        variant="outlined"
        startIcon={activeView ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ textTransform: 'none' }}
      >
        {label}
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => { setAnchor(null); setCreating(false); setNewName(''); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 240, maxWidth: 320 } }}
      >
        {views.length === 0 && !creating && (
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              No saved views yet. Save the current filters as a view to come back to later.
            </Typography>
          </Box>
        )}
        {views.map(view => {
          const isActive = view.id === activeView?.id;
          return (
            <MenuItem key={view.id} onClick={() => applyView(view)} selected={isActive}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {isActive ? <CheckIcon fontSize="small" color="primary" /> : <BookmarkBorderIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={view.name} primaryTypographyProps={{ variant: 'body2' }} />
              <Tooltip title="Delete view">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteView(view.id); }} sx={{ ml: 1 }}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          );
        })}
        {views.length > 0 && <Divider />}
        {creating ? (
          <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 0.5 }}>
            <TextField
              size="small" autoFocus
              placeholder="View name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCurrent(); if (e.key === 'Escape') { setCreating(false); setNewName(''); } }}
              sx={{ flex: 1 }}
            />
            <Button size="small" variant="contained" onClick={saveCurrent} disabled={!newName.trim()}>Save</Button>
          </Box>
        ) : (
          <MenuItem onClick={() => setCreating(true)}>
            <ListItemIcon sx={{ minWidth: 28 }}><AddIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Save current filters as view" primaryTypographyProps={{ variant: 'body2' }} />
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default SavedViews;
